import type { Breakpoint, TypographyProps } from "@carrotfertility/carotene-core"

/**
 * Where this page switches between the design's two compositions.
 *
 * S4 ships two drawings rather than a fluid range: a 968px desktop grid (`.grid` — 624px main plus a
 * 312px rail) and frame 9's 390px single column, which has its own markup entirely (`.mob`,
 * `.mob-card`, `.mob-chip`, `.mob-rail`). No frame sits between them, so the switch is a single
 * breakpoint and every site on the page has to agree on which one it is — hence this module rather
 * than a literal repeated in five files.
 *
 * `md` is 600px in Carotene's scale, which overrides MUI's stock values: `xs: 0, sm: 432, md: 600,
 * lg: 959, xl: 1200` (read out of the runtime bundle, not assumed). Chosen over `lg` because `lg` is
 * 959px, and neither a full-width CTA nor a single-column Past list is what you want across ~950px.
 * Above 600px the main column is still wide enough for the desktop composition even once the rail has
 * stacked below it — `PageLayout` does that at `lg`, which is deliberately a separate, earlier switch
 * that this page does not own.
 */
export const DESKTOP_BREAKPOINT = "md" as const

/**
 * A value that differs between frame 9 and the desktop frames, as an `sx` breakpoint object.
 *
 * Mobile-first: `xs` carries frame 9's value and `DESKTOP_BREAKPOINT` takes over from there.
 *
 * Preferred over `useMediaQuery` everywhere on this page it can express the difference, for one
 * reason beyond the usual (no listener, no resize re-render, no second paint): `setupTests.ts` stubs
 * `matchMedia` to answer `matches: false` to every query except `(pointer: fine)`, so in jsdom every
 * `useMediaQuery` resolves to its desktop branch. A mobile assertion written against a
 * `useMediaQuery` component therefore passes while testing desktop. CSS breakpoints are emitted into
 * the stylesheet whatever the viewport, so they can be asserted on directly — see
 * `cssForRenderedClasses` in `#/test-utils/emitted-css`, and `mobileLayout.test.tsx` for the shape.
 */
export function perViewport<T>(mobile: T, desktop: T): Partial<Record<Breakpoint, T>> {
  return { xs: mobile, [DESKTOP_BREAKPOINT]: desktop }
}

/**
 * A block whose whole type ROLE changes between the two compositions, as an `sx` value.
 *
 * Frame 9 does not scale the desktop type down — it reassigns it. A provider name is `.nm` (h5, 18px)
 * on desktop and `.mob-nm` (h6, 16px) at 390px; the meta and date lines go `.mt` (body1) → `.mob-mt`
 * (body2). Two named roles from the same scale, not one role at two sizes, which is why this returns
 * variant NAMES rather than font sizes: `sx={{ typography: perViewportTypography("h6", "h5") }}`
 * resolves each side against `theme.typography`, so the pair stays token-backed and a change to the
 * scale follows automatically.
 *
 * Paired with `variant` on the component rather than replacing it — `variant` picks the RENDERED TAG
 * (`variant="h5"` emits `<h5>`) and `sx.typography` only restyles it. Keeping both means the mobile
 * pass never moves an element's heading level, which `mobileLayout.test.tsx` counts on: it reads
 * visible past visits with `queryAllByRole("heading", { level: 5 })`.
 *
 * `typography` spreads the whole role, `color` included, so any `color` override in the same `sx` must
 * be written AFTER this key to win the cascade — see the disclaimer in `CarrotCompanionDetails`.
 */
export function perViewportTypography(
  mobile: TypographyProps["variant"],
  desktop: TypographyProps["variant"]
): Partial<Record<Breakpoint, TypographyProps["variant"]>> {
  return perViewport(mobile, desktop)
}
